import * as _ from 'lodash';
import type { Editor, Position } from 'codemirror';

import type { RevisionFileMatch } from '../../../../../hooks/api/revisions/useFileMatchesForRevision';

const NEWLINE_CHAR_LN = 1;

type HighlightOption = {
  start: Position;
  end: Position;
};

/**
 * Parses the source string and extracts the starting and ending positions.
 */
function getPositions(source: string) {
  const splitSource = source.split('-');
  const startingPosition = _.toFinite(splitSource[0]);
  const endPosition = _.toFinite(splitSource[1]);

  return { startingPosition, endPosition };
}

/**
 * Determines the highlighting start and end positions within the codeMirror editor.
 */
function getHighlightOption(source: string, codeMirror: Editor): HighlightOption | null {
  try {
    // Parse source for the positions.
    // The startingPosition is the first character position in the editor.
    // The endPosition is the last character position in editor.
    let { startingPosition, endPosition } = getPositions(source);
    let lineNumber = -1; // Code mirror's line number is zero-base.
    let lastLine;
    let start;

    /**
     * This while loop decrements the endPosition by the number of characters in each line
     * until it drops below zero. While the loop is running, it keeps track of the line numbers.
     *
     * Example:
     * Assume the editor has 3 lines as follows:
     * 1. "Hello "
     * 2. "world! "
     * 3. "How are you? "
     * If `startingPosition` is 10 and `endPosition` is 16, the loop should determine
     * the highlight starts at line 2, character 3 ("r") and ends at line 3, character 1 ("H").
     * Since the line number is zero-base, the output should be: {start : {line: 1, ch: 3}, end: {line: 2, ch: 1}}
     */
    while (endPosition > 0) {
      lineNumber++;
      const line = codeMirror.getLine(lineNumber);
      if (line == null) {
        endPosition = -1;
        lineNumber--;
      } else {
        lastLine = line;
        endPosition -= line.length + NEWLINE_CHAR_LN;
        startingPosition -= line.length + NEWLINE_CHAR_LN;
        if (!start && startingPosition <= 0) {
          start = {
            line: lineNumber,
            ch: line.length + startingPosition + NEWLINE_CHAR_LN,
          };
        }
      }
    }
    const end = {
      line: lineNumber,
      ch: endPosition + lastLine.length + NEWLINE_CHAR_LN,
    };

    return {
      start,
      end,
    };
  } catch (e) {
    // ignoring errors to avoid crashing the page.
    return null;
  }
}

/**
 * Returns an array of highlight options for the CodeMirror editor.
 */
export function getHighlightOptions(sources: RevisionFileMatch['sources'], codeMirror: Editor): HighlightOption[] {
  const highlightOptions: HighlightOption[] = [];
  (sources || []).forEach((source: string) => {
    const highlightOption = getHighlightOption(source, codeMirror);
    if (highlightOption) {
      highlightOptions.push(highlightOption);
    }
  });

  return highlightOptions;
}

/**
 * Jumps to a specific line number in the given CodeMirror editor and adds some padding to center the highlighted text.
 */
export function jumpToLineNumber(codeMirror: Editor, line: number): void {
  const drawer = document.getElementById('drawer');
  const currentWindowScroll = window.scrollY;
  const currentDrawerScroll = drawer?.scrollTop;
  const padding = 5; // add some padding so the highlighted text is centered.
  codeMirror.setCursor(line ? line + padding : 0);
  // It is important to restore the scroll position because codeMirror's setCursor function interferes with the scrolling.
  if (drawer) {
    // Reset scroll position in the drawer.
    drawer.scrollTop = currentDrawerScroll ?? 0;
  } else {
    // Reset scroll position in the full page details.
    window.scrollTo(0, currentWindowScroll);
  }
}
